import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, Mic2, Brain, BookOpen, LineChart, Target, Settings, ArrowRight, Home, Sparkles } from "lucide-react";
import { MEMORY_NODES } from "../data/mockData";

const QUICK_ACTIONS = [
  { id: "qa-session", label: "Start a live session", to: "/dashboard/session", icon: Mic2, accent: "cyan" },
  { id: "qa-today", label: "Go to Today", to: "/dashboard", icon: Home },
  { id: "qa-memory", label: "Open memory graph", to: "/dashboard/memory", icon: Brain },
  { id: "qa-practice", label: "Practice scenarios", to: "/dashboard/practice", icon: BookOpen },
  { id: "qa-progress", label: "See progress & CEFR", to: "/dashboard/progress", icon: LineChart },
  { id: "qa-goals", label: "Manage goals", to: "/dashboard/goals", icon: Target },
  { id: "qa-settings", label: "Open settings", to: "/dashboard/settings", icon: Settings },
];

const CommandPalette = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const memoryHits = useMemo(() => {
    if (!q.trim()) return [];
    const lower = q.toLowerCase();
    return MEMORY_NODES.filter((n) =>
      n.label.toLowerCase().includes(lower) ||
      n.detail.toLowerCase().includes(lower) ||
      n.tags.some((t) => t.toLowerCase().includes(lower))
    ).slice(0, 5);
  }, [q]);

  const actionHits = useMemo(() => {
    if (!q.trim()) return QUICK_ACTIONS;
    const lower = q.toLowerCase();
    return QUICK_ACTIONS.filter((a) => a.label.toLowerCase().includes(lower));
  }, [q]);

  const flatItems = useMemo(() => {
    const list = [];
    actionHits.forEach((a) => list.push({ kind: "action", ...a }));
    memoryHits.forEach((m) => list.push({ kind: "memory", ...m }));
    return list;
  }, [actionHits, memoryHits]);

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(flatItems.length - 1, i + 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
      if (e.key === "Enter") {
        const it = flatItems[active];
        if (!it) return;
        if (it.kind === "action") { onClose?.(); nav(it.to); }
        else if (it.kind === "memory") { onClose?.(); nav("/dashboard/memory"); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, flatItems, active, nav, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] grid place-items-start pt-[16vh] px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="command-palette"
        >
          <div className="absolute inset-0 bg-base/60 backdrop-blur-md" />
          <motion.div
            initial={{ y: -10, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[640px] rounded-3xl glass edge-glow overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-panel/8">
              <Search size={16} className="text-aura-cyan" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setActive(0); }}
                placeholder="Jump anywhere · search your memory…"
                className="flex-1 bg-transparent border-none outline-none text-[15px] text-ink placeholder:text-whisper-muted"
                data-testid="command-palette-input"
              />
              <span className="kbd">esc</span>
            </div>
            <div className="max-h-[420px] overflow-y-auto p-2">
              {actionHits.length > 0 && (
                <>
                  <div className="px-3 py-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">Quick actions</div>
                  {actionHits.map((a, i) => {
                    const idx = i;
                    return (
                      <button
                        key={a.id}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => { onClose?.(); nav(a.to); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left ${active === idx ? "bg-aura-cyan/[0.08]" : ""}`}
                      >
                        <span className="w-8 h-8 rounded-lg grid place-items-center bg-panel/[0.04] text-aura-cyan">
                          <a.icon size={14} strokeWidth={1.6} />
                        </span>
                        <span className="flex-1 text-[14px] text-ink/90">{a.label}</span>
                        <ArrowRight size={12} className="text-whisper-muted" />
                      </button>
                    );
                  })}
                </>
              )}

              {memoryHits.length > 0 && (
                <>
                  <div className="mt-2 px-3 py-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">Memory · {memoryHits.length} match</div>
                  {memoryHits.map((m, i) => {
                    const idx = actionHits.length + i;
                    return (
                      <button
                        key={m.id}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => { onClose?.(); nav("/dashboard/memory"); }}
                        className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left ${active === idx ? "bg-aura-cyan/[0.08]" : ""}`}
                      >
                        <span className="w-8 h-8 rounded-lg grid place-items-center bg-panel/[0.04] text-aura-cyan">
                          <Sparkles size={14} strokeWidth={1.6} />
                        </span>
                        <div className="flex-1">
                          <div className="text-[14px] text-ink/90">{m.label}</div>
                          <div className="text-[12px] text-whisper-muted line-clamp-1">{m.detail}</div>
                        </div>
                        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-whisper-muted">{m.cefr}</span>
                      </button>
                    );
                  })}
                </>
              )}

              {q && actionHits.length + memoryHits.length === 0 && (
                <div className="px-4 py-12 text-center text-whisper-muted text-[13px]">No matches. Try “past simple”, “market”, or “B2 by July”.</div>
              )}
            </div>
            <div className="flex items-center gap-3 px-4 py-3 border-t border-panel/8 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
              <span><span className="kbd">↑</span> <span className="kbd">↓</span> navigate</span>
              <span><span className="kbd">↵</span> select</span>
              <span className="ml-auto">AURA · command</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
